import { Button, cn } from '@ibe/ui';
import { type LucideIcon } from 'lucide-react';
import { type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

/**
 * The cell shapes every list in the portal shares.
 *
 * A table of one value per cell wastes the row: an operator scanning for a
 * property knows it by name *and* code, and putting the code in its own column
 * costs horizontal space that the columns they actually filter on need. Two
 * lines in the identifying cell is denser and reads faster.
 */

export interface PrimaryCellProps {
  /** The row's name. Becomes the link to its detail screen. */
  readonly label: string;
  readonly to: string;
  /** The identifier underneath — a code, a slug, an address. */
  readonly secondary?: string | null;
  readonly leading?: ReactNode;
}

export function PrimaryCell({
  label,
  to,
  secondary,
  leading,
}: PrimaryCellProps): React.JSX.Element {
  const { search } = useLocation();

  return (
    <div className="flex items-center gap-3">
      {leading}
      <div className="min-w-0">
        <Link
          to={
            to.includes('?')
              ? { pathname: to.slice(0, to.indexOf('?')), search: to.slice(to.indexOf('?') + 1) }
              : { pathname: to, search }
          }
          className="block truncate font-medium underline-offset-4 hover:underline focus-visible:underline"
        >
          {label}
        </Link>
        {secondary === undefined || secondary === null || secondary === '' ? null : (
          <span className="block truncate text-caption text-muted-foreground">{secondary}</span>
        )}
      </div>
    </div>
  );
}

export interface RowAction {
  readonly label: string;
  readonly icon: LucideIcon;
  /** A destination. Mutually exclusive with `onClick`. */
  readonly to?: string;
  readonly onClick?: () => void;
  readonly destructive?: boolean;
}

/**
 * The icon buttons at the end of a row.
 *
 * Every one carries a real label rather than a `title`: an icon-only control
 * with no accessible name is announced as "button", and a row of three of them
 * is announced as three buttons. `aria-label` is what makes them distinct, and
 * the tooltip a sighted user gets comes from the same string.
 *
 * The caller does the permission gating, because only it knows which
 * permission each action needs — and an action shown to someone who cannot
 * perform it is a button that fails on click.
 */
export function RowActions({
  actions,
}: {
  readonly actions: readonly RowAction[];
}): React.JSX.Element {
  const { search } = useLocation();

  return (
    <div className="flex flex-nowrap items-center justify-end gap-1">
      {actions.map((action) => {
        const className = cn(
          'h-8 w-8 shrink-0 p-0',
          action.destructive === true && 'text-destructive hover:text-destructive',
        );

        return action.to === undefined ? (
          <Button
            key={action.label}
            type="button"
            variant="ghost"
            size="sm"
            aria-label={action.label}
            title={action.label}
            className={className}
            onClick={action.onClick}
          >
            <action.icon className="h-4 w-4" aria-hidden="true" />
          </Button>
        ) : (
          <Button key={action.label} asChild variant="ghost" size="sm" className={className}>
            <Link
              to={
                action.to.includes('?')
                  ? {
                      pathname: action.to.slice(0, action.to.indexOf('?')),
                      search: action.to.slice(action.to.indexOf('?') + 1),
                    }
                  : { pathname: action.to, search }
              }
              aria-label={action.label}
              title={action.label}
            >
              <action.icon className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Button>
        );
      })}
    </div>
  );
}
